/**
 * @fileoverview Vuex mutations for live chat state management.
 *
 * @module store/modules/livechat/mutations
 *
 * Handles synchronous state updates for:
 * - Connected agents and socket status
 * - Active tab and selected chat tracking
 * - Queue, resolved, abandoned, and monitor chat lists
 * - Chat filters and loading states
 * - Typing indicators and reply objects
 * - Chat lifecycle (add, update, remove operations)
 */

import { MutationTree } from "vuex/types";

import { AgentStatusType, LivechatState, MessageStatus } from "./types";
import Vue from "vue";
import _ from "lodash";
import { getSelectedChatArrays } from "@/helperMethods/livechat/util";
import { cleanQueueChatObject, mergeLatestQueueChatObject } from "./utils";
import moment from "moment";

export type LivechatType = "monitor" | "queue" | "resolved" | "abandoned";

function getInteractionKeyId(interaction: any) {
  return interaction?.key_id || interaction?.meta?.key_id;
}

function getMessageStatusLevel(status?: MessageStatus) {
  switch (status) {
    case MessageStatus.SENDING:
      return 0;
    case MessageStatus.SENT:
      return 1;
    case MessageStatus.DELIVERED:
      return 2;
    case MessageStatus.READ:
      return 3;
    default:
      return -1;
  }
}

function getHighestMessageStatus(
  currentStatus?: MessageStatus,
  incomingStatus?: MessageStatus
) {
  if (!currentStatus) {
    return incomingStatus;
  }
  if (!incomingStatus) {
    return currentStatus;
  }
  if ([MessageStatus.ERROR, MessageStatus.FAILED].includes(incomingStatus)) {
    return incomingStatus;
  }
  if ([MessageStatus.ERROR, MessageStatus.FAILED].includes(currentStatus)) {
    return currentStatus;
  }
  return getMessageStatusLevel(currentStatus) > getMessageStatusLevel(incomingStatus)
    ? currentStatus
    : incomingStatus;
}

function applyKnownMessageStatuses(livechat: any, knownInteractions: any[] = []) {
  if (!Array.isArray(livechat?.interactions) || !knownInteractions.length) {
    return livechat;
  }

  const statusesByKey = knownInteractions.reduce((statuses, interaction) => {
    if (!interaction?.status) {
      return statuses;
    }
    const keys = _.compact([getInteractionKeyId(interaction), interaction.RowKey]);
    keys.forEach((key) => {
      statuses[key] = getHighestMessageStatus(statuses[key], interaction.status);
    });
    return statuses;
  }, {});

  return {
    ...livechat,
    interactions: livechat.interactions.map((interaction) => {
      const keyId = getInteractionKeyId(interaction);
      const knownStatus = (keyId ? statusesByKey[keyId] : null) || statusesByKey[interaction.RowKey];
      const status = getHighestMessageStatus(knownStatus, interaction.status);

      if (!status || status === interaction.status) {
        return interaction;
      }
      return {
        ...interaction,
        status,
      };
    }),
  };
}

function applyPendingMessageStatuses(livechat: any, pendingMessageStatuses: any) {
  if (!Array.isArray(livechat?.interactions)) {
    return livechat;
  }

  return {
    ...livechat,
    interactions: livechat.interactions.map((interaction) => {
      const keyId = getInteractionKeyId(interaction);
      const pendingStatus =
        (keyId ? pendingMessageStatuses[keyId] : null) ||
        pendingMessageStatuses[interaction.RowKey];

      if (!pendingStatus) {
        return interaction;
      }
      const status = getHighestMessageStatus(interaction.status, pendingStatus);
      return {
        ...interaction,
        status,
      };
    }),
  };
}

const mutations: MutationTree<LivechatState> = {
  SET_CONNECTED_AGENTS: (state, payload) => {
    Vue.set(state, "connectedAgents", payload);
  },
  INCREMENT_AGENT_ATTENDING: (state, agentEmail: string) => {
    const email = agentEmail?.toLowerCase?.();

    if (!email) return;

    const idx = state.connectedAgents.findIndex((a) => a.email?.toLowerCase?.() === email);

    if (idx < 0) return;

    const agent = state.connectedAgents[idx];
    const updated = { ...agent, attending: ((agent as any).attending ?? 0) + 1 };
    const arr = [...state.connectedAgents];

    arr[idx] = updated;

    Vue.set(state, "connectedAgents", arr);
  },
  DECREMENT_AGENT_ATTENDING: (state, agentEmail: string) => {
    const email = agentEmail?.toLowerCase?.();

    if (!email) return;

    const idx = state.connectedAgents.findIndex((a) => a.email?.toLowerCase?.() === email);

    if (idx < 0 || (state.connectedAgents[idx] as any).attending <= 0) return;

    const agent = state.connectedAgents[idx];
    const updated = { ...agent, attending: (agent as any).attending - 1 };
    const arr = [...state.connectedAgents];

    arr[idx] = updated;

    Vue.set(state, "connectedAgents", arr);
  },
  SET_SOCKET_CONNECTION_STATUS: (state, payload: string) => {
    Vue.set(state, "socketConnection", payload);
  },
  SET_AGENT_STATUS: (state, payload: AgentStatusType) => {
    Vue.set(state, "agentStatus", payload);
  },
  SET_ACTIVE_TAB_NAME(state, activeTabName) {
    Vue.set(state, "activeTabName", activeTabName);
  },
  SET_SELECTED_CHAT_ID(state, chatId) {
    Vue.set(state, "selectedChatId", chatId);
  },
  SELECT_MONITOR_CHAT(state, { partitionKey, rowKey }: { partitionKey: string; rowKey: string }) {
    const selectedChats = getSelectedChatArrays("monitor");

    const selectedChat = _.find(selectedChats, (chat) => {
      const currentRowKey = _.get(chat, "RowKey"); // Queue & Resolved
      const currentPartitionKey = _.get(chat, "PartitionKey");
      return currentRowKey === rowKey && currentPartitionKey === partitionKey;
    });

    if (!selectedChat) {
      Vue.set(state, "selectedMonitorChatPartitionKey", "");
      Vue.set(state, "selectedMonitorChatRowKey", "");
      return;
    }

    Vue.set(state, "selectedMonitorChatPartitionKey", partitionKey);
    Vue.set(state, "selectedMonitorChatRowKey", rowKey);
  },

  SELECT_MONITOR_CHAT_BY_SESSION_ID(state, sessionId: string) {
    const monitorChats = getSelectedChatArrays("monitor");
    const selectedChat = _.find(monitorChats, (chat) => {
      const chatSessionId = _.get(chat, "sessionId");
      return sessionId === chatSessionId;
    });
    if (!selectedChat) {
      Vue.set(state, "selectedMonitorChatPartitionKey", "");
      Vue.set(state, "selectedMonitorChatRowKey", "");
      return;
    }

    Vue.set(state, "selectedMonitorChatPartitionKey", _.get(selectedChat, "PartitionKey"));
    Vue.set(state, "selectedMonitorChatRowKey", _.get(selectedChat, "RowKey"));
  },

  SELECT_LIVE_CHAT(state, { chatId, type }: { chatId: string; type: LivechatType }) {
    const selectedChats = getSelectedChatArrays(type);

    const selectedChat: any = _.find(selectedChats, (chat) => {
      const currentChatId = _.get(chat, "RowKey", ""); // Queue & Resolved
      // const currentChatId = _.get(chat, "RowKey", chatSessionId); // Monitor
      return currentChatId === chatId;
    });

    if (!selectedChat) {
      Vue.set(state, "selectedChatId", "");
      return;
    }

    Vue.set(state, "selectedChatId", chatId);
  },

  SET_QUEUE_CHAT_OBJECT(state, { queueChatObject, count, ongoingCount }) {
    if (!_.isObjectLike(queueChatObject)) {
      console.warn("[SET_QUEUE_CHAT_OBJECT] queueChatObject is null or undefined");
      return;
    }

    // Remove resolved chats from the state to prevent the state data from becoming excessively large
    const cleanedChatObject = cleanQueueChatObject(state.queueChatObject, queueChatObject);

    // Ensure that the data fetched from /unresolvedSessions does not override the existing data unless it is more recent
    const newQueueChatObject = mergeLatestQueueChatObject(cleanedChatObject, queueChatObject);
    const queueChatObjectWithPendingStatuses = _.mapValues(newQueueChatObject, (chat) => {
      const chatWithKnownStatuses = applyKnownMessageStatuses(
        chat,
        queueChatObject[chat.RowKey]?.interactions
      );
      return applyPendingMessageStatuses(chatWithKnownStatuses, state.pendingMessageStatuses);
    });

    Vue.set(state, "queueChatObject", queueChatObjectWithPendingStatuses);
    Vue.set(
      state,
      "queueChatCount",
      _.isInteger(count) ? count : Object.keys(queueChatObjectWithPendingStatuses).length
    );
    Vue.set(
      state,
      "ongoingChatCount",
      _.isInteger(ongoingCount) ? ongoingCount : Object.keys(queueChatObjectWithPendingStatuses).length
    );
  },

  SET_RESOLVED_CHATS_ARRAY(state, { resolvedChatsArray, unfilteredResolvedChatsArray = [] }) {
    const stateResolvedChatsArray = _.cloneDeep(state.resolvedChatsArray);
    stateResolvedChatsArray.push(...resolvedChatsArray);
    Vue.set(state, "resolvedChatsArray", _.uniqBy(stateResolvedChatsArray, "RowKey"));
    const stateUnfilteredResolvedChatsArray = _.cloneDeep(state.unfilteredResolvedChatsArray);
    stateUnfilteredResolvedChatsArray.push(...unfilteredResolvedChatsArray);
    Vue.set(
      state,
      "unfilteredResolvedChatsArray",
      _.uniqBy(stateUnfilteredResolvedChatsArray, "RowKey")
    );
  },

  SET_RESOLVED_CHAT_COUNT(state, { count }) {
    Vue.set(state, "resolvedChatCount", count);
  },

  SET_ABANDONED_CHATS_ARRAY(state, { abandonedChatsArray, unfilteredAbandonedChatsArray = [] }) {
    const stateAbandonedChatsArray = _.cloneDeep(state.abandonedChatsArray);
    stateAbandonedChatsArray.push(...abandonedChatsArray);
    Vue.set(state, "abandonedChatsArray", _.uniqBy(stateAbandonedChatsArray, "RowKey"));
    const stateUnfilteredAbandonedChatsArray = _.cloneDeep(state.unfilteredAbandonedChatsArray);
    stateUnfilteredAbandonedChatsArray.push(...unfilteredAbandonedChatsArray);
    Vue.set(
      state,
      "unfilteredAbandonedChatsArray",
      _.uniqBy(stateUnfilteredAbandonedChatsArray, "RowKey")
    );
  },

  SET_ABANDONED_CHAT_COUNT(state, { count }) {
    Vue.set(state, "abandonedChatCount", count);
  },

  SET_MONITOR_CHATS_ARRAY(state, { monitorChatsArray }) {
    const stateMonitorChatsArray = _.cloneDeep(state.monitorChatsArray);
    stateMonitorChatsArray.push(...monitorChatsArray);
    Vue.set(state, "monitorChatsArray", stateMonitorChatsArray);
  },

  FLUSH_MONITOR_CHATS_ARRAY(state) {
    Vue.set(state, "monitorChatsArray", []);
  },

  FLUSH_RESOLVED_CHATS_ARRAY(state) {
    Vue.set(state, "resolvedChatsArray", []);
    Vue.set(state, "unfilteredResolvedChatsArray", []);
  },

  FLUSH_ABANDONED_CHATS_ARRAY(state) {
    Vue.set(state, "abandonedChatsArray", []);
    Vue.set(state, "unfilteredAbandonedChatsArray", []);
  },

  // Used for queue 'show more chats' function
  APPEND_QUEUE_CHAT_OBJECT(state, { queueChatObject }) {
    const newQueueChatObject = Object.assign({}, state.queueChatObject, queueChatObject);
    Vue.set(state, "queueChatObject", newQueueChatObject);
  },
  UPDATE_TYPING_INDICATOR_FOR_CHAT(state, { user_id, isTyping }) {
    Vue.set(state.typingIndicatorUserIds, user_id, isTyping);
  },
  SET_PENDING_MESSAGE_STATUS(state, { key_id, status }) {
    if (!key_id || !status) {
      return;
    }
    Vue.set(state, "pendingMessageStatuses", {
      ...state.pendingMessageStatuses,
      [key_id]: status,
    });
    const pendingMessageStatusKeys = Object.keys(state.pendingMessageStatuses);
    while (pendingMessageStatusKeys.length > 100) {
      const oldestKey = pendingMessageStatusKeys.shift();
      if (oldestKey) {
        Vue.delete(state.pendingMessageStatuses, oldestKey);
      }
    }
  },
  CLEAR_PENDING_MESSAGE_STATUS(state, { key_id }) {
    if (!key_id || !state.pendingMessageStatuses[key_id]) {
      return;
    }
    const pendingMessageStatuses = { ...state.pendingMessageStatuses };
    delete pendingMessageStatuses[key_id];
    Vue.set(state, "pendingMessageStatuses", pendingMessageStatuses);
  },
  UPDATE_QUEUE_CHAT(state, { updatedLivechat }) {
    // TODO: get livechats
    const chatId = updatedLivechat.RowKey;
    const targetLivechat = state.queueChatObject[chatId];
    if (targetLivechat) {
      // only update if the updated livechat is newer than the current chat
      if (
        targetLivechat?.last_modified && // not all emitted events have last modified
        (!moment(targetLivechat?.last_modified, moment.ISO_8601, true).isValid() ||
          !moment(updatedLivechat?.last_modified, moment.ISO_8601, true).isValid() ||
          moment(updatedLivechat?.last_modified) < moment(targetLivechat?.last_modified))
      ) {
        return;
      }

      // this helps to prevent the object assignment below from overwriting valid form data
      const updatedFormData = updatedLivechat.formData;
      const targetFormData = targetLivechat.formData;
      const newFormdata = Object.assign({}, updatedFormData, targetFormData);

      // formData order is swapped to ensure that input from visitor profile form is reflected
      // as it is overwritten in the above object assignment
      const newLivechatObject = Object.assign({}, targetLivechat, updatedLivechat, {
        formData: newFormdata,
      });
      const newLivechatObjectWithKnownStatuses = applyKnownMessageStatuses(
        newLivechatObject,
        targetLivechat.interactions
      );
      Vue.set(
        state.queueChatObject,
        chatId,
        applyPendingMessageStatuses(newLivechatObjectWithKnownStatuses, state.pendingMessageStatuses)
      );
    }
  },
  UPDATE_RESOLVED_CHAT(state, { updatedLivechat }) {
    const chatId = updatedLivechat.RowKey;
    const resolvedChats = _.cloneDeep(state.resolvedChatsArray);
    const targetLivechatIndex = resolvedChats.findIndex((chat) => chat.RowKey === chatId);
    if (targetLivechatIndex < 0) {
      return;
    }

    resolvedChats.splice(targetLivechatIndex, 1, updatedLivechat);
    Vue.set(state, "resolvedChatsArray", resolvedChats);
  },
  // updatedLivechat might contain more updated data,
  // replace the updatedLivechat into the corresponding chat in array
  UPDATE_ABANDONED_CHAT(state, { updatedLivechat }) {
    const chatId = updatedLivechat.RowKey;
    const abandonedChats = _.cloneDeep(state.abandonedChatsArray);
    const targetLivechatIndex = abandonedChats.findIndex((chat) => chat.RowKey === chatId);
    if (targetLivechatIndex < 0) {
      return;
    }

    abandonedChats.splice(targetLivechatIndex, 1, updatedLivechat);
    Vue.set(state, "abandonedChatsArray", abandonedChats);
  },
  UPDATE_MONITOR_CHAT(state, { updatedLivechat }) {
    const chatId = updatedLivechat.RowKey;
    const targetLivechatIndex = _.findIndex(
      state.monitorChatsArray,
      (chatObj) => chatObj.RowKey === chatId
    );
    if (targetLivechatIndex === -1) {
      return;
    }
    const targetLiveChat = state.monitorChatsArray[targetLivechatIndex];
    const newLivechatObject = Object.assign({}, targetLiveChat, updatedLivechat);
    Vue.set(state.monitorChatsArray, targetLivechatIndex, newLivechatObject);
  },

  UPDATE_MONITOR_CHATS(state, { updatedLivechat }) {
    // TODO: get livechats
    // get the one livechat element
    // update array
    // set array back into state
  },

  REMOVE_QUEUE_CHAT(state, { resolvedChatId }) {
    const newQueueChatObject = _.cloneDeep(state.queueChatObject);
    if (newQueueChatObject[resolvedChatId]) {
      newQueueChatObject[resolvedChatId].isRemoved = true;
      Vue.set(state, "queueChatObject", newQueueChatObject);
    }
  },

  ADD_QUEUE_CHAT(state, { newChat }) {
    const existingChat = state.queueChatObject[newChat.RowKey];
    const chatWithKnownStatuses = applyKnownMessageStatuses(newChat, existingChat?.interactions);
    const chatWithPendingStatuses = applyPendingMessageStatuses(
      chatWithKnownStatuses,
      state.pendingMessageStatuses
    );
    const newQueueChatObject = {
      ...state.queueChatObject,
      [newChat.RowKey]: chatWithPendingStatuses,
    };

    Vue.set(state, "queueChatObject", newQueueChatObject);
  },

  ADD_RESOLVED_CHAT(state, { resolvedChat }) {
    const stateResolvedChatsArray = _.cloneDeep(state.resolvedChatsArray);
    if (stateResolvedChatsArray.length > 0) {
      stateResolvedChatsArray.unshift(resolvedChat.chat);
      Vue.set(state, "resolvedChatsArray", stateResolvedChatsArray);
    }
  },

  INCREMENT_RESOLVED_CHAT_TOTAL_COUNT(state) {
    Vue.set(state, "resolvedChatCount", state.resolvedChatCount + 1);
  },

  ADD_ABANDONED_CHAT(state, { abandonedChat }) {
    const stateAbandonedChatsArray = _.cloneDeep(state.abandonedChatsArray);
    if (stateAbandonedChatsArray.length > 0) {
      stateAbandonedChatsArray.unshift(abandonedChat.chat);
      Vue.set(state, "abandonedChatsArray", stateAbandonedChatsArray);
    }
  },

  INCREMENT_ABANDONED_CHAT_TOTAL_COUNT(state) {
    Vue.set(state, "abandonedChatCount", state.abandonedChatCount + 1);
  },

  SET_FETCH_QUEUE_CHAT_LOADING(state, isLoading) {
    Vue.set(state, "fetchQueueChatLoading", isLoading);
  },
  SET_FETCH_RESOLVED_CHAT_LOADING(state, isLoading) {
    Vue.set(state, "fetchResolvedChatLoading", isLoading);
  },
  SET_FETCH_ABANDONED_CHAT_LOADING(state, isLoading) {
    Vue.set(state, "fetchAbandonedChatLoading", isLoading);
  },
  SET_FETCH_MONITOR_CHAT_LOADING(state, isLoading) {
    Vue.set(state, "fetchMonitorChatLoading", isLoading);
  },
  RESET_ALL_FILTERS(state, payload) {
    const defaultFilters = state.defaultFilters;
    const resolvedChatFilters = state.resolvedChatFilters;
    const abandonedChatFilters = state.abandonedChatFilters;
    Vue.set(state, "queueChatFilters", {
      searchTerm: "",
      chatSources: [],
    });
    Vue.set(state, "resolvedChatFilters", {
      ...resolvedChatFilters,
      agentIsNot: "[]",
      includeInteractions: false,
    });
    Vue.set(state, "abandonedChatFilters", {
      ...abandonedChatFilters,
      agentIs: "[]",
      includeInteractions: false,
    });
    Vue.set(state, "monitorChatFilters", defaultFilters);
  },

  UPDATE_QUEUE_LIVECHAT_FILTERS(state, queueChatFilter) {
    Vue.set(state, "queueChatFilters", queueChatFilter);
  },
  UPDATE_RESOLVED_LIVECHAT_FILTERS(state, resolvedChatFilter) {
    Vue.set(state, "resolvedChatFilters", {
      ...resolvedChatFilter,
      agentIsNot: "[]",
      includeInteractions: false,
    });
  },
  UPDATE_ABANDONED_LIVECHAT_FILTERS(state, abandonedChatFilter) {
    Vue.set(state, "abandonedChatFilters", {
      ...abandonedChatFilter,
      agentIs: "[]",
      includeInteractions: false,
    });
  },
  UPDATE_MONITOR_LIVECHAT_FILTERS(state, monitorChatFilter) {
    Vue.set(state, "monitorChatFilters", monitorChatFilter);
  },
  UPDATE_ALL_FIELD_DATA(state, payload) {
    Vue.set(state, "fieldData", payload);
  },
  SET_REPLY(state, payload) {
    if (!payload) {
      Vue.set(state, "reply", {
        id: null,
        key_id: null,
        type: null,
        from: null,
        previewContent: "",
      });
      return;
    }
    const from =
      (payload?.type === "message" || payload?.type === "postback"
        ? "user"
        : payload?.type === "reply"
        ? "bot"
        : payload?.type) || "N/A";
    const type = payload?.data?.content?.[0]?.images
      ? "image"
      : payload?.data?.content?.[0]?.files
      ? "file"
      : "text";
    let previewContent = type === "text" ? payload?.data?.content?.[0]?.text : "";
    if (type === "file") {
      const fileItem = payload?.data?.content?.[0]?.files?.[0] || {};
      const { url = fileItem } = fileItem;
      previewContent = url.split("/").pop();
    }
    if (type === "image") {
      const imgItem = payload?.data?.content?.[0]?.images?.[0] || {};
      const { url = imgItem } = imgItem;
      previewContent = url.split("/").pop();
    }

    const newReply = {
      id: payload?.RowKey,
      key_id: null,
      type,
      from,
      previewContent,
    };
    Vue.set(state, "reply", newReply);
  },
};

export default mutations;
